import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OrderExtractedList from "@/components/whatsapp-monitoring/order-extracted-list";
import { buildMetadata } from "@/lib/metadata";
import { OrderService } from "@/services/order-services";
import { WhapiService } from "@/services/whapi-service";
import { Link, createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_main/whatsapp-monitoring/$groupId")({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "WhatsApp Monitoring - Details",
      description: "WhatsApp Monitoring Details",
      keywords: ["whatsapp", "monitoring", "details"],
    }),
  }),
  loader: async ({ params }) => {
    // First fetch all whatsapp groups
    const groups = await WhapiService.getGroups();

    const group = groups.find((g) => g.id === Number(params.groupId));

    if (!group) {
      throw new Error(`Group with ID ${params.groupId} not found`);
    }

    // Create a copy of the group with enhanced messages that include order data
    const groupWithOrders = {
      ...group,
      messages: await Promise.all(
        group.messages?.map(async (message) => {
          if (typeof message?.order === "number") {
            const orderData = await OrderService.getOrderById(
              message.order.toString(),
            );
            return {
              ...message,
              orderData,
            };
          }

          return {
            ...message,
            orderData: null,
          };
        }),
      ),
    };

    return {
      group: groupWithOrders,
    };
  },
});

function RouteComponent() {
  const { group } = Route.useLoaderData();
  const [searchQuery, setSearchQuery] = useState("");
  const [groupBy, setGroupBy] = useState<"day" | "week" | "month">("day");
  const [orderOnly, setOrderOnly] = useState(false);

  // Filter messages based on search query
  const filteredMessages = group.messages.filter((message) => {
    if (searchQuery.trim() === "") {
      return true;
    }

    // Search in message content
    const messageTextMatch = message.content?.some((item) =>
      item.text?.body?.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    // Search in order data if present
    const orderMatch = message.orderData
      ? JSON.stringify(message.orderData)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      : false;

    return messageTextMatch || orderMatch;
  });

  return (
    <div className="space-y-6 p-6">
      <Button variant="link" className="mb-8">
        <ChevronLeft />
        <Link to="/whatsapp-monitoring">
          Go back to Whatsapp Monitoring List
        </Link>
      </Button>

      <div>
        <h2 className="font-semibold text-2xl">Conversation History</h2>
        <span className="text-muted-foreground">
          Viewing all messages from{" "}
          {group.messages?.[0]?.created_at
            ? format(new Date(group.messages[0].created_at), "MMMM d, yyyy")
            : "Unknown date"}{" "}
          to{" "}
          {group.messages?.length &&
          group.messages[group.messages.length - 1]?.created_at
            ? format(
                new Date(
                  group.messages[group.messages.length - 1]
                    .created_at as string,
                ),
                "MMMM d, yyyy",
              )
            : "Unknown date"}
        </span>
      </div>

      <div className="max-w-xs">
        <div className="space-y-4">
          <h3 className="mb-2 font-medium text-lg">Filter Messages</h3>
          <Input
            placeholder="Search messages"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex items-center gap-2">
            <Checkbox
              id="include-orders"
              checked={orderOnly}
              onCheckedChange={(checked) => setOrderOnly(checked === true)}
            />
            <Label htmlFor="include-orders">
              Show only order-related messages
            </Label>
          </div>

          <Select
            defaultValue="day"
            onValueChange={(value) =>
              setGroupBy(value as "day" | "week" | "month")
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Group by" defaultValue={"day"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Group by day</SelectItem>
              <SelectItem value="week">Group by week</SelectItem>
              <SelectItem value="month">Group by month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Empty div to maintain the grid layout */}
        <div />
      </div>

      <OrderExtractedList
        messages={filteredMessages}
        groupBy={groupBy}
        orderOnly={orderOnly}
      />

      {/* <pre className="rounded-lg bg-muted p-4 font-mono text-sm">
        {JSON.stringify(group, null, 2)}
      </pre> */}
    </div>
  );
}
